// src/admin/screens/borrowers/tabs/CreditScoreTab.tsx
// Renders the current score / tier and a recent event history. Events
// describe every delta (loan_repaid, payment_late, kyc_verified, etc.);
// operators use this to explain a score in a support conversation.
//
// credit_scores / credit_score_events are keyed by user_id which (per
// migration 06) references profiles.id — so we pass borrowerId (the
// profile row id) straight through.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import Card from '@admin/components/Card';
import TierBadge from '@admin/components/TierBadge';
import { formatCatDateTime } from '@admin/utils/dates';
import type { Database } from '@/lib/database.types';

type Tables = Database['public']['Tables'];
type ScoreRow = Tables['credit_scores']['Row'];
type EventRow = Tables['credit_score_events']['Row'];

export default function CreditScoreTab({ borrowerId }: { borrowerId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['credit-score', borrowerId],
    queryFn: async (): Promise<{ score: ScoreRow | null; events: EventRow[] }> => {
      const [scoreRes, eventsRes] = await Promise.all([
        supabase.from('credit_scores').select('*').eq('user_id', borrowerId).maybeSingle(),
        supabase
          .from('credit_score_events')
          .select('*')
          .eq('user_id', borrowerId)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);
      if (scoreRes.error) throw scoreRes.error;
      if (eventsRes.error) throw eventsRes.error;
      return { score: scoreRes.data, events: eventsRes.data ?? [] };
    },
  });

  if (isLoading) return <p className="small">Loading credit score…</p>;
  if (error) return <p style={{ color: 'var(--c-danger)' }}>{(error as Error).message}</p>;
  if (!data) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16 }}>
      <Card title="Current score">
        {data.score ? (
          <>
            <div
              className="tabular"
              style={{
                fontSize: 48,
                fontWeight: 800,
                color: 'var(--c-primary-900)',
                lineHeight: 1,
              }}
            >
              {data.score.current_score}
            </div>
            <div style={{ marginTop: 8 }}>
              <TierBadge tier={data.score.current_tier} />
            </div>
            <p className="small" style={{ marginTop: 12 }}>
              Updated {formatCatDateTime(data.score.updated_at)}
            </p>
          </>
        ) : (
          <p className="small">No score on file.</p>
        )}
      </Card>
      <Card title="Recent events">
        {data.events.length === 0 ? (
          <p className="small">No scoring events yet.</p>
        ) : (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
            aria-label="Credit score event history"
          >
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>When</th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'left' }}>Reason</th>
                <th style={{ textAlign: 'right' }}>Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid var(--c-border-lt)' }}>
                  <td style={{ padding: '6px 0' }}>{formatCatDateTime(e.created_at)}</td>
                  <td>{e.event_type}</td>
                  <td>{e.reason}</td>
                  <td
                    className="tabular"
                    style={{
                      textAlign: 'right',
                      color: e.delta >= 0 ? 'var(--c-success)' : 'var(--c-danger)',
                    }}
                  >
                    {e.delta >= 0 ? '+' : ''}
                    {e.delta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
